import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import api from "../services/api"
import FileIcon from "./FileIcon"
import { Icon } from "../trust-center/ui/primitives"
import AuthSteps from "./AuthSteps"

const REASON_MAX_LENGTH = 200

/**
 * "Request access" flow. Authentication (email/OTP/registration) is delegated
 * to <AuthSteps>, the same component TrustLoginModal uses for plain sign-in —
 * previously this modal re-implemented that whole flow independently, with a
 * different field order and visual style, which made "Login" and "Request
 * access" feel like two different products.
 */
export default function AuthModal({
    isOpen,
    onClose,
    selectedDocs = [],
    onAuthSuccess,
    toast,
    user = null,
    onRequestSent,
}) {
    const navigate = useNavigate()

    // "auth"          — signing the user in (skipped if already logged in)
    // "nda-required"  — prospect authenticated but hasn't signed the NDA yet
    // "reason"        — collect the reason for the request and submit it
    const [phase, setPhase] = useState(user ? "reason" : "auth")
    const [authStep, setAuthStep] = useState("form")
    const [requestReason, setRequestReason] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [inlineError, setInlineError] = useState("")

    useEffect(() => {
        if (!isOpen) return
        setPhase(user ? "reason" : "auth")
        setRequestReason("")
        setInlineError("")
    }, [isOpen, user])

    if (!isOpen) return null

    const selectedDoc = selectedDocs[0]

    const handleAuthenticated = (userData) => {
        onAuthSuccess?.(userData)
        if (userData.role === "prospect" && !userData.ndaSigned) {
            setPhase("nda-required")
            return
        }
        const alreadyHasAccess = userData.accessibleDocs?.some((id) =>
            selectedDocs.some((d) => String(d._id) === String(id))
        )
        if (alreadyHasAccess || userData.role === "admin") {
            onClose()
            return
        }
        setPhase("reason")
    }

    const handleRequestAccess = async (e) => {
        e.preventDefault()
        setInlineError("")
        if (selectedDocs.length === 0) { setInlineError("No documents selected. Please try selecting the documents again."); return }
        if (!requestReason.trim()) { setInlineError("Please provide a reason for your request."); return }
        if (requestReason.length > REASON_MAX_LENGTH) {
            setInlineError(`Reason must be ${REASON_MAX_LENGTH} characters or fewer.`)
            return
        }
        setIsSubmitting(true)
        try {
            for (const doc of selectedDocs) {
                try {
                    await api.post("/documents/request", {
                        documentId: doc._id,
                        reason: requestReason,
                    })
                } catch (err) {
                    // 409 means request already exists — treat as success, not error
                    if (err.response?.status !== 409) throw err
                }
            }
            toast?.success("Access request sent to admin")
            onRequestSent?.()
            onClose()
            setRequestReason("")
        } catch (err) {
            console.error(err)
            setInlineError(err.response?.data?.message || "Failed to send request. Please try again.")
        } finally {
            setIsSubmitting(false)
        }
    }

    if (phase === "auth") {
        return (
            <div className="overlay" onClick={onClose}>
                <div className={`modal${authStep === "register" ? " modal--register" : ""}`} onClick={(e) => e.stopPropagation()}>
                    <div className="modal__head">
                        <span className="modal__ico"><Icon name="padlock-locked" size={22} /></span>
                        <div>
                            <h3>Get access to Darwinbox's Trust Center</h3>
                        </div>
                        <button className="modal__x" onClick={onClose} aria-label="Close"><Icon name="close" size={18} /></button>
                    </div>
                    <div className="modal__body">
                        <AuthSteps onAuthenticated={handleAuthenticated} onCancel={onClose} onStepChange={setAuthStep} />
                    </div>
                </div>
            </div>
        )
    }

    const isDocRequestForm = phase === "reason"

    return (
        <div className="overlay">
            <div className={`modal${isDocRequestForm ? " modal--access-request" : ""}`} style={isDocRequestForm ? undefined : { maxWidth: "540px", padding: "10px" }} role={isDocRequestForm ? "dialog" : undefined} aria-modal={isDocRequestForm ? "true" : undefined} aria-labelledby={isDocRequestForm ? "access-request-title" : undefined}>
                <div className={`modal-hd${isDocRequestForm ? " modal-hd--access-request" : ""}`} style={isDocRequestForm ? undefined : { border: "none", paddingBottom: 0 }}>
                    {isDocRequestForm ? (
                        <h2 id="access-request-title" className="req-access__title">Request access</h2>
                    ) : (
                        <div className="modal-title" style={{ width: "100%", textAlign: "center" }}>
                            Request Access
                        </div>
                    )}
                    <button className="modal-x" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="modal-body" style={isDocRequestForm ? undefined : { textAlign: "center", padding: "0 30px 30px" }}>
                    {inlineError && (
                        <div style={{
                            background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "8px",
                            padding: "10px 14px", marginBottom: "16px", fontSize: "13px",
                            color: "#b91c1c", textAlign: "left", fontWeight: "500",
                        }}>
                            {inlineError}
                        </div>
                    )}

                    {phase === "nda-required" && (
                        <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "40px", marginBottom: "10px" }}>📝</div>
                            <h3 className="font-bold mb-2">NDA Required</h3>
                            <p className="text-sm text-gray-600 mb-6">
                                Before requesting access to private documents, you need to sign our Non-Disclosure Agreement.
                                This is a one-time process.
                            </p>
                            <p className="text-xs text-gray-400 mb-4">
                                After signing, return here to request access to specific documents.
                            </p>
                            <button className="btn btn-blue-pill w-full" onClick={() => {
                                onClose()
                                navigate("/nda", { replace: true })
                            }}>Sign NDA</button>
                        </div>
                    )}

                    {phase === "reason" && (
                        <div className="req-access" role="form" aria-labelledby="access-request-title">
                            <div className="req-access__doc">
                                <div className="req-access__doc-icon" aria-hidden="true">
                                    <FileIcon size={22} color="#0183FF" />
                                </div>
                                <div className="req-access__doc-info">
                                    <span className="req-access__doc-label">
                                        {selectedDocs.length > 1 ? `${selectedDocs.length} documents selected` : "Document"}
                                    </span>
                                    {selectedDocs.length > 1 ? (
                                        <div className="req-access__doc-chips">
                                            {selectedDocs.map(doc => (
                                                <span key={doc._id} className="req-access__chip">{doc.title}</span>
                                            ))}
                                        </div>
                                    ) : (
                                        <>
                                            <span className="req-access__doc-name">{selectedDoc?.title}</span>
                                            {selectedDoc?.category && (
                                                <span className="req-access__doc-badge">{selectedDoc.category}</span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="req-access__field">
                                <div className="req-access__field-header">
                                    <label htmlFor="access-request-reason" className="req-access__label">
                                        Why do you need access? <span className="req-access__required" aria-hidden="true">*</span>
                                    </label>
                                    <span className="req-access__count" aria-live="polite">
                                        {requestReason.length} / {REASON_MAX_LENGTH}
                                    </span>
                                </div>
                                <textarea
                                    id="access-request-reason"
                                    className="req-access__input"
                                    value={requestReason}
                                    onChange={(e) => setRequestReason(e.target.value.slice(0, REASON_MAX_LENGTH))}
                                    maxLength={REASON_MAX_LENGTH}
                                    required
                                    placeholder="Briefly describe your reason — e.g. audit requirement, vendor assessment, compliance review"
                                    autoFocus
                                />
                                <p className="req-access__hint">
                                    Our team will review your request and notify you by email, usually within one business day.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {phase === "reason" && (
                    <div className="modal-ft modal-ft--access-request">
                        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleRequestAccess}
                            disabled={isSubmitting || !requestReason.trim()}
                        >
                            {isSubmitting ? "Submitting…" : "Submit request"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
